import { CommonModule, KeyValuePipe } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTagsModule } from '@vendasta/galaxy/tags';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { Action, AssistantType, NamespaceType } from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  AiAssignmentConstraintForm,
  AiConstraintsForm,
  CONSTRAINT_CONFIG,
} from '../../../core/forms/ai-constraints.form';

@Component({
  selector: 'ai-constraints-field',

  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    TranslateModule,
    GalaxyFormFieldModule,
    GalaxyTagsModule,
    GalaxyTooltip,
    KeyValuePipe,
  ],
  templateUrl: './ai-constraints-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-constraints-field.component.scss',
})
export class AiConstraintsFieldComponent {
  private translate = inject(TranslateService);

  constraintsForm = input.required<AiConstraintsForm>();
  allowedActions = input<Action[]>([Action.ACTION_ASSIGN, Action.ACTION_VIEW, Action.ACTION_INVOKE]);

  assistantNamespaceTypes = CONSTRAINT_CONFIG.assistantNamespaceTypes;
  assistantTypes = CONSTRAINT_CONFIG.assistantTypes;
  effects = CONSTRAINT_CONFIG.effects;
  mediums = CONSTRAINT_CONFIG.mediums;
  channels = CONSTRAINT_CONFIG.channels;
  availableChannels = Array.from(CONSTRAINT_CONFIG.channels.values());
  authStates = CONSTRAINT_CONFIG.authStates;
  apps = CONSTRAINT_CONFIG.apps;

  isActionAllowed(action: Action): boolean {
    return this.allowedActions().includes(action);
  }

  protected readonly Action = Action;

  readonly singleAction = computed<boolean>(() => this.allowedActions().length === 1);

  addAssignmentConstraint(): void {
    this.constraintsForm()?.addAssignmentConstraint();
  }

  removeAssignmentConstraint(index: number): void {
    this.constraintsForm()?.removeAssignmentConstraint(index);
  }

  addInvokeConstraint(): void {
    this.constraintsForm()?.addInvokeConstraint();
  }

  removeInvokeConstraint(index: number): void {
    this.constraintsForm()?.removeInvokeConstraint(index);
  }

  onScopeTypeChange(index: number, scopeType: string): void {
    const constraintForm = this.constraintsForm()?.controls.assignmentConstraints.at(
      index,
    ) as AiAssignmentConstraintForm;
    if (constraintForm) {
      if (scopeType === 'assistantNamespaceType') {
        constraintForm.controls.scopeValue.setValue(NamespaceType.NAMESPACE_TYPE_ACCOUNT_GROUP);
      } else if (scopeType === 'assistantType') {
        constraintForm.controls.scopeValue.setValue(AssistantType.ASSISTANT_TYPE_SYSTEM);
      }
    }
  }
}
